import {
  Injectable,
  PLATFORM_ID,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { EnvToken } from '@app/core/env.tokens';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';
import { VerticalContent } from '@models/vertical/verticalContent.model';

export interface MiniVerticalIndexCategory {
  asCat: number;
  slug: string;
  title?: string;
  categoryLvl?: number;
}

export interface MiniVerticalIndexEntry {
  _id?: string;
  slug: string;
  newSlug?: string;
  asCat: number;
  h1?: string;
  tT?: string;
  viewCount?: number;
  path: string;
}

export interface MiniVerticalIndexSubCategory extends MiniVerticalIndexCategory {
  count: number;
  indexPath: string;
}

export interface MiniVerticalIndex {
  category: MiniVerticalIndexCategory;
  mainCategory: MiniVerticalIndexCategory;
  parentCategory?: MiniVerticalIndexCategory;
  indexPath: string;
  miniVerticals: MiniVerticalIndexEntry[];
  subCategories: MiniVerticalIndexSubCategory[];
}

export interface MiniVertical {
  _id?: string;
  slug: string;
  cat: string;
  h1?: string;
  tT?: string;
  task?: string;
  heroImg?: string;
  imgAltAttribute?: string;
  content?: VerticalContent;
  url: string;
  // Not returned by /api/mini-verticals/:slug — the hub category arrives in
  // `relatedCategory` instead. Kept optional as a defensive fallback.
  mainCategory?: {
    slug: string;
    title: string;
  };
  /**
   * When true the page emits `robots: noindex, follow`, and the API omits it
   * from sitemaps and internal link lists. Flags mini-service pages that earned
   * neither a Google impression nor an attributed task over a trailing 12
   * months. The page still renders and returns 200 — this is a reversible index
   * decision, not a removal.
   */
  noindex?: boolean;
  mT?: string;
  mDescription?: string;
  /**
   * `mDescription` plus a price band ("… Fra 5.000 kr."), for the meta
   * description only. Deliberately NOT rendered on the page: `mDescription`
   * doubles as the visible hero subtitle, and city URLs concatenate the city
   * name after it, where a trailing price would read oddly. Absent where the
   * price was implausible or the result would exceed the description limit.
   *
   * @deprecated Fallback only. The price in this field is `price_guide.min`
   * verbatim, and that guide is shared by up to 1,091 mini-services, so it is a
   * category band rather than a price for this service — measured above the
   * average of real bids on 22.5% of pages. `composeMetaDescription` prefers a
   * sentence derived from this document's own stored bids and falls back here
   * only when there are none.
   */
  mDescriptionMeta?: string;
  /**
   * A shortened, meta-only rewrite of `mDescription`, carrying no price of its
   * own. Exists so a price sentence can fit inside the 160-char budget without
   * touching `mDescription`, which renders in full as the visible hero subtitle
   * (`HeroSectionComponent.subtitleText`) — trimming that would change page copy,
   * not just meta.
   *
   * Written by `shorten-mini-vertical-meta-fields.ts` in handyhand-dashboard for
   * the pages where the full copy leaves no room. Absent everywhere else, and
   * `composeMetaDescription` prefers the full text whenever that still fits.
   */
  mDescriptionShort?: string;
  mFAQ?: string;
  mProvide?: string;
  mWhy?: string;
  mKnow?: string;
  mNeed?: string;
  mBestHH?: string;
  asCat?: number;
  isActive?: boolean;
  status?: string;
  author?: string;
  date?: Date;
  lastModifiedAt?: Date;
  viewCount?: number;
  relatedVertical?: {
    slug: string;
    cat: string;
    title: string;
    url?: string;
  };
  relatedCategory?: {
    asCat: string;
    slug: string;
    title: string;
  };
  relatedCategoryData?: {
    asCat: number;
    cat: string;
    slug: string;
  };
  taskExamples?: any[];
  similarTasks?: any[];
  aiPriceguideId?: string;
  aiPriceguideSlug?: string;
  aiPriceguideTitle?: string;
  aiPriceGuideUrl?: string;
  priceGuideId?: string;
  price_guide?: any;
  mostRelevantPriceGuide?: any;
  substantivering?: string;
  s1?: string;
  s2?: string;
  s3?: string;
  s4?: string;
  s5?: string;
  sP?: string;
  sPM?: string;
  titleTagWithGeo?: string;
  tagId?: string;
  taskStatistics?: any;
  proofRead?: string;
  n8nProduced?: boolean;
  dbTag?: string;
}

// The dashboard API recomputes this index on its own cache misses, which can
// take 10s+ — far past the 2.5s SSR HTTP budget. Give the availability probe
// a much tighter budget: the hub-page link it feeds is a nice-to-have, and on
// timeout the browser re-checks after hydration with its own 12s budget.
const SSR_AVAILABILITY_TIMEOUT_MS = 800;

// Availability flips only when mini-verticals are published/unpublished for a
// category, so memoize per worker to keep repeat hub renders from re-fetching
// the full index (up to ~400 KB) on every request. Keyed by full URL because
// one worker serves multiple country hosts with different API bases.
const SSR_AVAILABILITY_TTL_MS = 60 * 60 * 1000;
const ssrAvailabilityCache = new Map<
  string,
  { value: boolean; expires: number }
>();

@Injectable({ providedIn: 'root' })
export class MiniVerticalService {
  protected readonly env = inject(EnvToken);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  baseHref = this.env.dashboardBaseRef;
  apiPrefix = this.env.apiPrefix;

  constructor(private http: HttpClient) {}

  fetchMiniVerticalById(slug: string): Observable<MiniVertical> {
    const url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/${slug}`;
    return this.http.get<MiniVertical>(url);
  }

  fetchOverview(asCat?: number): Observable<MiniVertical[]> {
    let url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/overview`;
    if (asCat) {
      url += `?asCat=${asCat}`;
    }
    return this.http.get<MiniVertical[]>(url);
  }

  fetchActive(): Observable<MiniVertical[]> {
    const url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/active`;
    return this.http.get<MiniVertical[]>(url);
  }

  // This method fetches mini verticals as an Index page by the parent Category association
  fetchMiniVerticalsByCategorySlug(
    activeCategorySlug: string,
  ): Observable<MiniVertical[]> {
    const categorySlug = activeCategorySlug?.toLowerCase().trim();
    const url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/index-by-main-category-slug/${categorySlug}`;
    return this.http.get<MiniVertical[]>(url);
  }

  fetchMiniVerticalIndexByCategorySlug(
    categorySlugInput: string,
  ): Observable<MiniVerticalIndex> {
    const categorySlug = categorySlugInput?.toLowerCase().trim();
    const url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/index-by-category-slug/${categorySlug}`;
    return this.http.get<MiniVerticalIndex>(url);
  }

  // Availability probe for the hub page's mini-vertical index link. The full
  // index response is reduced to a boolean and handed to the client via
  // TransferState; the `availabilityCheck=1` marker keeps the raw response out
  // of the HTTP transfer cache (see the filter in app.config.ts) so the page
  // HTML doesn't carry hundreds of KB of unused entity data.
  hasMiniVerticalsByCategorySlug(
    activeCategorySlug: string,
  ): Observable<boolean> {
    const categorySlug = activeCategorySlug?.toLowerCase().trim();
    const url = `${this.baseHref}api/${this.apiPrefix}mini-verticals/index-by-main-category-slug/${categorySlug}?availabilityCheck=1`;
    const stateKey = makeStateKey<boolean>(`hasMiniVerticals:${categorySlug}`);

    if (!this.isServer && this.transferState.hasKey(stateKey)) {
      return of(this.transferState.get(stateKey, false));
    }

    if (this.isServer) {
      const cached = ssrAvailabilityCache.get(url);
      if (cached && cached.expires > Date.now()) {
        this.transferState.set(stateKey, cached.value);
        return of(cached.value);
      }
    }
    const result = this.http
      .get<MiniVertical[]>(url)
      .pipe(map((miniVerticals) => !!miniVerticals?.length));

    if (!this.isServer) {
      return result;
    }
    return result.pipe(
      timeout(SSR_AVAILABILITY_TIMEOUT_MS),
      tap((value) => {
        ssrAvailabilityCache.set(url, {
          value,
          expires: Date.now() + SSR_AVAILABILITY_TTL_MS,
        });
        this.transferState.set(stateKey, value);
      }),
    );
  }
}
